import React, { useState } from 'react';
import ChatService from '../services/ChatService';
import './MediaGalleryModal.css';

/**
 * MediaGalleryModal - Displays all media files (images, videos, audio) from a chat
 * Impressive UI with grid layout and full-screen viewer
 */

/**
 * MediaGalleryModal - Displays all media files (images, videos, audio) from a chat
 */
const MediaGalleryModal = ({ mediaFiles, roomId, onClose }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'image', 'video', 'audio'

  // Filter media by type
  const filteredMedia = filter === 'all' 
    ? mediaFiles 
    : mediaFiles.filter(m => m.messageType === filter);

  // Get full URL for media
  const getFullUrl = (fileUrl) => {
    if (!fileUrl) return null;
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://') || fileUrl.startsWith('blob:')) {
      return fileUrl;
    }
    return ChatService.getFullFileUrl(fileUrl, roomId);
  };

  const handleMediaClick = (media) => {
    setSelectedMedia(media);
  };

  const handleClose = () => {
    setSelectedMedia(null);
    onClose();
  };

  return (
    <div className="media-gallery-overlay" onClick={handleClose}>
      <div className="media-gallery-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="media-gallery-header">
          <h2 className="media-gallery-title">Media, Links, and Docs</h2>
          <button className="media-gallery-close" onClick={handleClose}>
            <span className="material-icons-outlined">close</span>
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="media-gallery-filters">
          <button
            className={`media-filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({mediaFiles.length})
          </button>
          <button
            className={`media-filter-btn ${filter === 'image' ? 'active' : ''}`}
            onClick={() => setFilter('image')}
          >
            <span className="material-icons-outlined">image</span>
            Photos ({mediaFiles.filter(m => m.messageType === 'image').length})
          </button>
          <button
            className={`media-filter-btn ${filter === 'video' ? 'active' : ''}`}
            onClick={() => setFilter('video')}
          >
            <span className="material-icons-outlined">videocam</span>
            Videos ({mediaFiles.filter(m => m.messageType === 'video').length})
          </button>
          <button
            className={`media-filter-btn ${filter === 'audio' ? 'active' : ''}`}
            onClick={() => setFilter('audio')}
          >
            <span className="material-icons-outlined">audiotrack</span>
            Audio ({mediaFiles.filter(m => m.messageType === 'audio').length})
          </button>
        </div>

        {/* Media Grid */}
        <div className="media-gallery-grid">
          {filteredMedia.length === 0 ? (
            <div className="media-gallery-empty">
              <span className="material-icons-outlined" style={{ fontSize: '3rem', color: '#a8a29e', marginBottom: '1rem' }}>
                {filter === 'image' ? 'image' : filter === 'video' ? 'videocam' : filter === 'audio' ? 'audiotrack' : 'collections'}
              </span>
              <p style={{ color: '#78716c', fontSize: '0.875rem' }}>
                No {filter === 'all' ? 'media' : filter + 's'} found
              </p>
            </div>
          ) : (
            filteredMedia.map((media, index) => {
              const fullUrl = getFullUrl(media.fileUrl);
              
              return (
                <div
                  key={media.id || index}
                  className="media-gallery-item"
                  onClick={() => handleMediaClick(media)}
                >
                  {media.messageType === 'image' ? (
                    <img
                      src={fullUrl}
                      alt={media.fileName || 'Image'}
                      className="media-gallery-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : media.messageType === 'video' ? (
                    <div className="media-gallery-thumbnail video-thumbnail">
                      <video src={fullUrl} className="media-gallery-video-preview" />
                      <span className="material-icons-outlined media-play-icon">play_circle</span>
                    </div>
                  ) : media.messageType === 'audio' ? (
                    <div className="media-gallery-thumbnail audio-thumbnail">
                      <span className="material-icons-outlined" style={{ fontSize: '2rem', color: '#0d9488' }}>
                        audiotrack
                      </span>
                    </div>
                  ) : (
                    <div className="media-gallery-thumbnail file-thumbnail">
                      <span className="material-icons-outlined" style={{ fontSize: '2rem', color: '#78716c' }}>
                        insert_drive_file
                      </span>
                    </div>
                  )}
                  <div className="media-gallery-error" style={{ display: 'none' }}>
                    <span className="material-icons-outlined">broken_image</span>
                  </div>
                  <div className="media-gallery-overlay-info">
                    <span className="material-icons-outlined media-gallery-icon">
                      {media.messageType === 'image' ? 'image' : 
                       media.messageType === 'video' ? 'videocam' : 
                       media.messageType === 'audio' ? 'audiotrack' : 'insert_drive_file'}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Full Screen Media Viewer */}
      {selectedMedia && (
        <div className="media-viewer-overlay" onClick={() => setSelectedMedia(null)}>
          <div className="media-viewer-container" onClick={(e) => e.stopPropagation()}>
            <button className="media-viewer-close" onClick={() => setSelectedMedia(null)}>
              <span className="material-icons-outlined">close</span>
            </button>
            {selectedMedia.messageType === 'image' ? (
              <img
                src={getFullUrl(selectedMedia.fileUrl)}
                alt={selectedMedia.fileName || 'Image'}
                className="media-viewer-image"
              />
            ) : selectedMedia.messageType === 'video' ? (
              <video
                src={getFullUrl(selectedMedia.fileUrl)}
                controls
                className="media-viewer-video"
              />
            ) : selectedMedia.messageType === 'audio' ? (
              <div className="media-viewer-audio">
                <span className="material-icons-outlined" style={{ fontSize: '4rem', color: '#0d9488', marginBottom: '1rem' }}>
                  audiotrack
                </span>
                <audio
                  src={getFullUrl(selectedMedia.fileUrl)}
                  controls
                  style={{ width: '100%', maxWidth: '400px' }}
                />
                <p style={{ marginTop: '1rem', color: '#78716c', fontSize: '0.875rem' }}>
                  {selectedMedia.fileName || 'Audio file'}
                </p>
              </div>
            ) : (
              <div className="media-viewer-file">
                <span className="material-icons-outlined" style={{ fontSize: '4rem', color: '#78716c', marginBottom: '1rem' }}>
                  insert_drive_file
                </span>
                <p style={{ marginBottom: '1rem', color: '#1c1917', fontSize: '1rem' }}>
                  {selectedMedia.fileName || 'File'}
                </p>
                <a
                  href={getFullUrl(selectedMedia.fileUrl)}
                  download={selectedMedia.fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#0d9488',
                    color: 'white',
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#115e59'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0d9488'}
                >
                  Download File
                </a>
              </div>
            )}
            <div className="media-viewer-info">
              <p style={{ margin: 0, color: '#78716c', fontSize: '0.875rem' }}>
                {selectedMedia.fileName || 'File'}
              </p>
              {selectedMedia.createdAt && (
                <p style={{ margin: '0.25rem 0 0 0', color: '#a8a29e', fontSize: '0.75rem' }}>
                  {new Date(selectedMedia.createdAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGalleryModal;

